import { useMemo, useState } from 'react';

import { BookModal } from '@/features/library/components/BookModal.js';
import { useMyShelf } from '@/features/library/hooks/useMyShelf.js';
import { useUpcomingFromFavoriteAuthors } from '@/features/library/hooks/useUpcomingFromFavoriteAuthors.js';
import { useUpcomingReleases } from '@/features/library/hooks/useUpcomingReleases.js';

import { AuthorsList } from '../components/AuthorsList.js';
import { FollowedGenresCard } from '../components/FollowedGenresCard.js';
import { MonthReleasesList } from '../components/MonthReleasesList.js';
import { ReleasesTimeline } from '../components/ReleasesTimeline.js';
import { UpcomingBookcase } from '../components/UpcomingBookcase.js';
import type { UpcomingItem } from '../types.js';

const MONTH_SHORT = [
  'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin',
  'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc',
];

const MONTH_LONG_FORMAT = new Intl.DateTimeFormat('fr-FR', {
  month: 'long',
  year: 'numeric',
});

const NOW = new Date();

/**
 * Page Sorties — combine deux sources pour ne pas être vide quand la
 * wishlist est légère :
 *  1. `useUpcomingReleases` — wishlist du user avec releaseDate future
 *     (intent explicite : "j'attends ce livre").
 *  2. `useUpcomingFromFavoriteAuthors` — prochaines parutions des auteurs
 *     présents dans la shelf du user (intent latent : "voici ce qui sort
 *     chez tes auteurs habituels").
 * Les deux sources sont fusionnées et dédupliquées par bookId. Les wishlist
 * items sont prioritaires (gardent leur entryId).
 */
export function UpcomingPage(): JSX.Element {
  const { data: wishlistData } = useUpcomingReleases(100);
  const { data: authorsData } = useUpcomingFromFavoriteAuthors(40);
  const { data: shelfData } = useMyShelf();
  const [year, setYear] = useState(NOW.getFullYear());
  const [month, setMonth] = useState(NOW.getMonth());
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null);
  const [openingBookId, setOpeningBookId] = useState<string | null>(null);

  const allItems: ReadonlyArray<UpcomingItem> = useMemo(() => {
    const map = new Map<string, UpcomingItem>();
    for (const entry of wishlistData?.items ?? []) {
      map.set(entry.book.id, { id: entry.id, book: entry.book });
    }
    for (const item of authorsData?.items ?? []) {
      if (map.has(item.book.id)) continue;
      map.set(item.book.id, { id: `author-${item.book.id}`, book: item.book });
    }
    return Array.from(map.values());
  }, [wishlistData, authorsData]);

  const yearItems = useMemo(() => {
    return allItems.filter((it) => {
      if (!it.book.releaseDate) return false;
      return new Date(it.book.releaseDate).getFullYear() === year;
    });
  }, [allItems, year]);

  const monthCounts = useMemo(() => {
    const counts = new Array<number>(12).fill(0);
    for (const it of yearItems) {
      if (!it.book.releaseDate) continue;
      const idx = new Date(it.book.releaseDate).getMonth();
      counts[idx] = (counts[idx] ?? 0) + 1;
    }
    return counts;
  }, [yearItems]);

  const monthItems = useMemo(() => {
    return yearItems
      .filter(
        (it) =>
          it.book.releaseDate !== null &&
          new Date(it.book.releaseDate).getMonth() === month,
      )
      .sort((a, b) => {
        const ta = a.book.releaseDate
          ? new Date(a.book.releaseDate).getTime()
          : Number.POSITIVE_INFINITY;
        const tb = b.book.releaseDate
          ? new Date(b.book.releaseDate).getTime()
          : Number.POSITIVE_INFINITY;
        return ta - tb;
      });
  }, [yearItems, month]);

  const selectedDate = new Date(year, month, 1);
  const monthTitle = capitalize(MONTH_LONG_FORMAT.format(selectedDate));

  const selectedEntry =
    shelfData?.items.find((e) => e.book.id === selectedBookId) ?? null;
  const selectedBook =
    selectedEntry?.book ??
    allItems.find((it) => it.book.id === selectedBookId)?.book ??
    null;

  const handleBookClick = (bookId: string): void => {
    if (openingBookId !== null) return;
    setOpeningBookId(bookId);
    window.setTimeout(() => setSelectedBookId(bookId), 380);
  };

  const handleClose = (): void => {
    setSelectedBookId(null);
    setOpeningBookId(null);
  };

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-brown md:text-4xl xl:text-5xl">
            Sorties à venir
          </h1>
          <p className="mt-1 font-script text-lg text-brown-soft xl:text-xl">
            les nouvelles parutions 🗓️
          </p>
        </div>
        <div className="flex items-center gap-2 font-serif font-semibold text-brown">
          <button
            type="button"
            onClick={() => setYear((y) => y - 1)}
            aria-label="Année précédente"
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-paper shadow-sm hover:bg-cream-dark"
          >
            ‹
          </button>
          <span className="text-lg">{year}</span>
          <button
            type="button"
            onClick={() => setYear((y) => y + 1)}
            aria-label="Année suivante"
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-paper shadow-sm hover:bg-cream-dark"
          >
            ›
          </button>
        </div>
      </header>

      <div className="calendar-strip">
        {MONTH_SHORT.map((m, idx) => {
          const count = monthCounts[idx] ?? 0;
          const active = idx === month;
          return (
            <button
              key={m}
              type="button"
              onClick={() => setMonth(idx)}
              className={`cal-month${active ? ' active' : ''}`}
              aria-pressed={active}
            >
              {m}
              <span className="cal-month-sub">{count}</span>
            </button>
          );
        })}
      </div>

      <ReleasesTimeline entries={yearItems} />

      <div className="calendar-grid">
        <div className="flex flex-col gap-6">
          <section className="flex flex-col gap-3">
            <div>
              <h2 className="font-serif text-xl font-semibold text-brown">
                {monthTitle}
              </h2>
              <p className="font-script text-base text-brown-soft">
                tes auteurs suivis
              </p>
            </div>
            <MonthReleasesList
              entries={monthItems}
              onEntryClick={(it) => handleBookClick(it.book.id)}
            />
          </section>

          <section className="flex flex-col gap-3">
            <div>
              <h2 className="font-serif text-xl font-semibold text-brown">
                Rayon "Bientôt"
              </h2>
              <p className="font-script text-base text-brown-soft">
                tes suites attendues 🐲
              </p>
            </div>
            <UpcomingBookcase
              entries={yearItems}
              openingBookId={openingBookId}
              onBookClick={(b) => handleBookClick(b.id)}
            />
          </section>
        </div>

        <aside className="flex flex-col gap-5">
          <AuthorsList entries={allItems} />
          <FollowedGenresCard />
        </aside>
      </div>

      {selectedBook && (
        <BookModal
          book={selectedBook}
          entry={selectedEntry}
          onClose={handleClose}
        />
      )}
    </div>
  );
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
}
